import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { AppButton } from "../ui";
import "./marketing.scss";

/**
 * OAuth landing page for ad-platform grants (Google today).
 *
 * Runs inside the popup the Ad Accounts page opened. Its only job is to hand the
 * authorization code to the backend and report what happened — the opener
 * re-reads from the server when this window closes, so there is no message to
 * pass back.
 *
 * ⚠️ **Meta never lands here.** Its grant is redeemed by the shared
 * WhatsApp/lead-capture callback so that exactly one code path mints a Meta
 * token; two would drift on scopes and the symptom would be leads quietly
 * stopping. The route accepts a `:provider` param anyway, and a Meta code sent
 * here gets a clear message rather than a second redemption.
 */
const MarketingConnectCallback = () => {
  const { provider } = useParams();
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: "working", message: "" });

  // React 18 StrictMode double-invokes effects in development, and an OAuth
  // code is single-use — the second exchange always fails with "code already
  // redeemed" and would overwrite a successful result with an error.
  const redeemedRef = useRef(false);

  useEffect(() => {
    if (redeemedRef.current) return;
    redeemedRef.current = true;

    const code = params.get("code");
    const error = params.get("error_description") || params.get("error");

    if (error) {
      setState({ status: "error", message: error });
      return;
    }
    if (!code) {
      setState({ status: "error", message: "No authorization code was returned." });
      return;
    }

    (async () => {
      try {
        const { data } = await apiClient.post(`/marketing/connections/${provider}/callback`, {
          code,
          redirectUri: `${window.location.origin}/marketing/connect/${provider}`,
        });
        setState({ status: "ok", message: data.message || "Connected." });
      } catch (e) {
        setState({
          status: "error",
          message: e?.response?.data?.message || "The connection could not be completed.",
        });
      }
    })();
  }, [provider, params]);

  const Icon = state.status === "ok" ? CheckCircle2 : state.status === "error" ? XCircle : Loader;

  return (
    <div className="mkt-callback">
      <Icon
        size={40}
        className={`mkt-callback__icon mkt-callback__icon--${state.status}`}
      />
      <h3 className="mkt-callback__title">
        {state.status === "working" && "Finishing the connection…"}
        {state.status === "ok" && "Connected"}
        {state.status === "error" && "Could not connect"}
      </h3>
      {state.message && <p className="mkt-callback__text">{state.message}</p>}

      {state.status !== "working" && (
        <AppButton onClick={() => window.close()}>Close this window</AppButton>
      )}
    </div>
  );
};

export default MarketingConnectCallback;
